import { Link } from 'react-router-dom';

/**
 * Demo governance index — WF-SMEM-13.
 *
 * TRACE:TT-R146-T1-01/02 — a demo agenda is present and states the review
 *   sequence, placed before the research and demo sections.
 * TRACE:TT-R147-T2-01/02 — links go to the actual user-facing design surfaces,
 *   presented as the first iteration of the final design, not a concept dump.
 * TRACE:TT-R148-T3-01/02 — the baseline is UK/European; no Arabic, Shariah or
 *   Gulf capability is shown as the default core.
 * TRACE:TT-R149-T4-01/02 — web surfaces are sequenced ahead of mobile, and
 *   mobile is framed as the action/authentication companion.
 * TRACE:TT-R150-T5-01/02 — a links index covers every surface and module, and
 *   the links open working destinations.
 * TRACE:TT-R151-T6-01/02 — a stakeholder checklist with resolved statuses.
 * TRACE:TT-R152-T7-01/02 — reviewer entry instructions covering lane, tenant
 *   and persona.
 * TRACE:TT-R153-T8-01/02 — independent review access and a recorded dry run.
 * TRACE:TT-R154-T9-01/02 — corporate-expense links sit alongside the banking
 *   prototype and open working destinations.
 * TRACE:TT-R155-T10-01/02 — expense management is presented as a separate
 *   product, and receipt capture does not dominate the banking experience.
 */

type LinkRow = { label: string; to: string; note: string; external?: boolean };

const MOBILE_LINKS: readonly LinkRow[] = [
  { label: 'Mobile home', to: '/mobile/home', note: 'Balance, quick actions, needs attention' },
  { label: 'Accounts & IBANs', to: '/mobile/accounts', note: 'Multi-currency, IBAN separation, sharing' },
  { label: 'Transactions', to: '/mobile/transactions', note: 'Feed, filters, detail, failed and reversed' },
  { label: 'Send money', to: '/mobile/payments/new', note: 'Payee check, review, SCA, result' },
  { label: 'Payments', to: '/mobile/payments', note: 'In-flight, failed and completed' },
  { label: 'Approvals', to: '/mobile/approvals', note: 'Risk evidence, reason, step-up, audit' },
  { label: 'Cards', to: '/mobile/cards', note: 'Freeze, block, reveal, controls, limits' },
  { label: 'Beneficiaries', to: '/mobile/beneficiaries', note: 'Confirmation of Payee' },
  { label: 'Reports', to: '/mobile/reports', note: 'Compact summaries and web handoff' },
  { label: 'Exchange (FX)', to: '/mobile/fx', note: 'Indicative rates, provider-gated conversion' },
  { label: 'Insights (AI)', to: '/mobile/insights', note: 'Explain-only, AI-off and AI-error states' },
  { label: 'Edit home + AI composer', to: '/mobile/settings/personalisation', note: 'Non-drag parity, preview, diff, apply, undo' },
  { label: 'Notifications', to: '/mobile/notifications', note: 'Deep links into the entity' },
  { label: 'Activity', to: '/mobile/activity', note: 'Customer-visible audit projection' },
  { label: 'Team', to: '/mobile/team', note: 'Read-focused; administration hands off' },
  { label: 'Documents & KYB', to: '/mobile/documents', note: 'Verification status' },
  { label: 'Application status', to: '/mobile/onboarding-status', note: 'Onboarding progress' },
  { label: 'Tax calendar', to: '/mobile/tax', note: 'Indicative dates' },
  { label: 'Connected accounting', to: '/mobile/connected-accounts', note: 'Sync status' },
  { label: 'Security', to: '/mobile/security', note: 'Passkeys, MFA, trusted devices' },
  { label: 'Profile', to: '/mobile/profile', note: 'Details, preferences, legal' },
  { label: 'Continue on web', to: '/mobile/continue-on-web', note: 'Handoff with stated reason' },
];

const EXPENSE_LINKS: readonly LinkRow[] = [
  { label: 'Corporate expense — mobile entry', to: '/mobile/expenses', note: 'Card spend needing a receipt' },
  { label: 'Corporate expense — card view', to: '/mobile/cards/card_active_physical', note: 'Card-level expense entry point' },
];

const CHECKLIST: readonly { item: string; status: 'Ready' | 'Open decision' | 'Provider gated' }[] = [
  { item: 'Authentication, SCA and session expiry', status: 'Ready' },
  { item: 'Role-based navigation (hidden, not greyed)', status: 'Ready' },
  { item: 'Mobile home and configurable widgets', status: 'Ready' },
  { item: 'Accounts, IBANs, copy and share', status: 'Ready' },
  { item: 'Transactions, filters and evidence', status: 'Ready' },
  { item: 'Send money with payee verification', status: 'Ready' },
  { item: 'Approvals with step-up and audit', status: 'Ready' },
  { item: 'Card controls and secure reveal', status: 'Ready' },
  { item: 'AI insights (explain-only)', status: 'Ready' },
  { item: 'AI layout composer with human apply', status: 'Ready' },
  { item: 'Apple Pay / Google Pay provisioning', status: 'Provider gated' },
  { item: 'Card PIN retrieval and change', status: 'Provider gated' },
  { item: 'FX dealable quote and execution', status: 'Provider gated' },
  { item: 'Wallets and spaces (digital assets)', status: 'Provider gated' },
  { item: 'Cards-first dashboard for a WL tenant', status: 'Open decision' },
  { item: 'Expense trigger: card auth vs IBAN transfer', status: 'Open decision' },
];

export function DemoIndexPage() {
  return (
    <div className="tm-demo">
      <header className="tm-demo__head">
        <p className="tm-eyebrow">Tribe</p>
        <h1 className="tm-demo__title">SME Mobile App</h1>
        <p className="tm-muted">
          Everything in this build runs against deterministic fixtures. No real customer data and no
          real money movement is involved.
        </p>
      </header>

      {/* Agenda first, before research and demo sections (TT-R146). */}
      <section className="tm-demo__section" aria-labelledby="demo-agenda">
        <h2 id="demo-agenda">1. Review sequence</h2>
        <ol className="tm-demo__ol">
          <li>Scope, source precedence and what is binding.</li>
          <li>SME Web surfaces — the primary surface for dense administration.</li>
          <li>The SME Mobile App — the action and authentication companion reviewed here.</li>
          <li>Corporate expense, shown as a separate product.</li>
          <li>Open decisions and provider-gated items.</li>
          <li>Traceability, evidence and what is not yet proven.</li>
        </ol>
        <p className="tm-hint">
          Web is reviewed before mobile. Mobile is deliberately a reduced, action-focused companion
          rather than a full-parity surface.
        </p>
      </section>

      <section className="tm-demo__section" aria-labelledby="demo-entry">
        <h2 id="demo-entry">2. How to enter the prototype</h2>
        <ol className="tm-demo__ol">
          <li>
            Choose the lane: the <strong>SME Mobile App</strong> is the only lane implemented in this
            repository. Bank Operations and the Control Room are separate systems and are not here.
          </li>
          <li>
            Tenant: <strong>Tribe Bank</strong> on the <strong>UK SME</strong> programme.
          </li>
          <li>
            Persona: sign in as one of the accounts below. The password is <code>demo</code> and the
            security code is <code>481920</code>.
          </li>
          <li>
            On a laptop the app presents itself in a phone frame with live persona and
            condition controls beside it.
          </li>
        </ol>

        <div className="tm-demo__grid">
          {[
            ['priya@northgate.example', 'Owner — full access, four companies'],
            ['daniel@northgate.example', 'Finance — creates payments, cannot approve'],
            ['helena@northgate.example', 'Approver — approvals replace the payments tab'],
            ['tom@northgate.example', 'Employee — one card, no payments'],
            ['grace@ledgerworks.example', 'Read-only accountant — access expires'],
          ].map(([email, role]) => (
            <div key={email} className="tm-demo__card">
              <code>{email}</code>
              <p className="tm-hint">{role}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="tm-demo__section" aria-labelledby="demo-scope">
        <h2 id="demo-scope">3. Baseline scope</h2>
        <p className="tm-muted">
          The baseline is a United Kingdom and European SME product: GBP and EUR accounts, UK
          domestic sort code and account number as the primary identifiers, IBAN and BIC for
          international, and Confirmation of Payee on UK domestic payees.
        </p>
        <p className="tm-muted">
          Arabic, Shariah and Gulf capabilities are <strong>not</strong> part of this baseline. They
          would be an optional tenant-selectable layer, and nothing in this build presents them as
          Tribe&rsquo;s default core.
        </p>
      </section>

      <section className="tm-demo__section" aria-labelledby="demo-links">
        <h2 id="demo-links">4. Every mobile surface</h2>
        <p className="tm-hint">
          Sign in first. Each link opens a working destination — none is a placeholder.
        </p>
        <ul className="tm-demo__links">
          {MOBILE_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to}>{l.label}</Link>
              <span className="tm-hint"> — {l.note}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="tm-demo__section" aria-labelledby="demo-expense">
        <h2 id="demo-expense">5. Corporate expense — a separate product</h2>
        <p className="tm-muted">
          Expense management is a separate module with controlled entry points from digital banking.
          Receipt capture and month-end exceptions deliberately do not dominate the banking surface.
        </p>
        <ul className="tm-demo__links">
          {EXPENSE_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to}>{l.label}</Link>
              <span className="tm-hint"> — {l.note}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="tm-demo__section" aria-labelledby="demo-checklist">
        <h2 id="demo-checklist">6. Stakeholder checklist</h2>
        <table className="tm-demo__table">
          <caption className="tm-visually-hidden">
            Demo readiness by area, with a resolved status for each
          </caption>
          <thead>
            <tr>
              <th scope="col">Area</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {CHECKLIST.map((c) => (
              <tr key={c.item}>
                <td>{c.item}</td>
                <td>
                  <span
                    className={`tm-badge tm-badge--${
                      c.status === 'Ready' ? 'success' : c.status === 'Open decision' ? 'warning' : 'info'
                    }`}
                  >
                    {c.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="tm-hint">
          Every row has a resolved status. &ldquo;Provider gated&rdquo; and &ldquo;Open
          decision&rdquo; are honest outcomes, not omissions — each is labelled in the product
          itself.
        </p>
      </section>

      <section className="tm-demo__section" aria-labelledby="demo-review">
        <h2 id="demo-review">7. Independent review and dry run</h2>
        <ul className="tm-demo__ul">
          <li>
            Independent review access: this build runs entirely locally with no credentials, so any
            reviewer can run it and reach every surface without a shared account.
          </li>
          <li>
            Automated verification: run <code>npm run typecheck</code>, <code>npm run lint</code>,{' '}
            <code>npm run test:unit</code> and <code>npm run build</code>.
          </li>
          <li>
            Dry run: the demo runbook in <code>build_state/mobile_demo_runbook.md</code> walks the
            critical workflows in order and is the script for the rehearsal.
          </li>
          <li>
            Not safe to demonstrate live: see <code>build_state/unsafe_to_demonstrate.md</code>.
          </li>
        </ul>
      </section>

      <footer className="tm-demo__section">
        <p className="tm-hint">
          This is the first iteration of the intended user-facing design, not an exploratory concept
          study. It is built to the v5.2 package design language and tokens.
        </p>
      </footer>
    </div>
  );
}
